import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { PAGE_SIZE, STATUS, UNLICENSED_ORIGIN } from '~/literals';

import { FlightSearchRequestParams, FlightsSearchState, ErrorResponse, PriceConfimationParameters, FlightSearchFilters, Offer, Paging } from '~/types';
import { FlightSearchResponse } from '~/types/flights-search';

export const flightsSearchState: FlightsSearchState = {
  flightSearchRespose: null,
  alertMessage: '',
  pageStatus: STATUS.IDLE,
  searchParams: null,
  selectedOffersPriceCheckReady: false,
  offersToView: [],
  lazyLoadedOffers: [],
  availableAirlines: [],
  filters: {},
  filterStatus: STATUS.IDLE,
  lazyLoadingStatus: STATUS.IDLE,
  paging: {
    currentPage: 0,
    pageSize: PAGE_SIZE,
  },
  unLicensedOrigin: false,
};

export const flightsSlice = createSlice({
  name: 'flightSearch',
  initialState: flightsSearchState,
  reducers: {
    getFlightOffers: (draft, _: PayloadAction<FlightSearchRequestParams>) => {
      draft.selectedOffersPriceCheckReady = false;
      draft.alertMessage = '';
      draft.flightSearchRespose = null;
      draft.offersToView = [];
      draft.lazyLoadedOffers = [];
      draft.pageStatus = STATUS.RUNNING;
      draft.paging.currentPage = 0;
      draft.paging.pageSize = PAGE_SIZE;
      draft.selectedOffersPriceCheckReady = false;
      draft.unLicensedOrigin = false;
    },
    getFlightsSuccess: (draft, { payload }: PayloadAction<FlightSearchResponse>) => {
      draft.selectedOffersPriceCheckReady = false;
      draft.flightSearchRespose = payload;
      draft.offersToView = payload.offers;
      draft.pageStatus = STATUS.SUCCESS;
    },
    getFlightsFailure: (draft, { payload }: PayloadAction<ErrorResponse>) => {
      if (payload?.errorMessage?.toLocaleLowerCase()?.includes(UNLICENSED_ORIGIN.toLocaleLowerCase())) {
        draft.unLicensedOrigin = true;
      }
      draft.selectedOffersPriceCheckReady = false;
      draft.alertMessage = payload.errorMessage || 'Something went wrong.';
      draft.pageStatus = STATUS.ERROR;
      draft.flightSearchRespose = null;
    },
    filterOffers: (draft, { payload }: PayloadAction<FlightSearchFilters>) => {
      draft.filterStatus = STATUS.RUNNING;
      draft.filters = payload;
      draft.offersToView = [];
      draft.lazyLoadedOffers = [];
      draft.paging.currentPage = 0;
      draft.paging.pageSize = PAGE_SIZE;
    },
    filterSuccess: (draft, { payload }: PayloadAction<Offer[]>) => {
      draft.offersToView = payload;
      draft.filterStatus = STATUS.SUCCESS;
    },
    filterFailure: (draft, { payload }: PayloadAction<ErrorResponse>) => {
      draft.alertMessage = payload.errorMessage || 'Something went wrong.';
      draft.filterStatus = STATUS.ERROR;
      draft.offersToView = [];
    },
    filterReset: (draft) => {
      draft.filterStatus = STATUS.IDLE;
    },
    lazyLoadOffers: (draft, _: PayloadAction<Paging>) => {
      draft.lazyLoadingStatus = STATUS.RUNNING;
    },
    lazyLoadOffersSucess: (draft, { payload }: PayloadAction<Offer[]>) => {
      draft.lazyLoadedOffers = payload;
      draft.paging.currentPage = payload.length ? draft.paging.currentPage + 1 : draft.paging.currentPage;
      draft.lazyLoadingStatus = STATUS.SUCCESS;
    },
    lazyLoadOffersFailure: (draft) => {
      draft.lazyLoadingStatus = STATUS.ERROR;
    },
    getFlightOffersPriceConfimation: (draft, _: PayloadAction<PriceConfimationParameters>) => {
      draft.pageStatus = STATUS.RUNNING;
    },
    getFlightsPriceConfimationSuccess: (draft) => {
      draft.selectedOffersPriceCheckReady = true;
      draft.pageStatus = STATUS.READY;
      draft.flightSearchRespose = null;
    },
    getFlightsPriceConfimationFailure: (draft, { payload }: PayloadAction<ErrorResponse>) => {
      draft.selectedOffersPriceCheckReady = false;
      draft.alertMessage = payload.errorMessage || 'Something went wrong.';
      draft.pageStatus = STATUS.ERROR;
    },
    resetOfferSearchData: (draft) => {
      draft.flightSearchRespose = null;
      draft.alertMessage = '';
      draft.pageStatus = STATUS.IDLE;
      draft.searchParams = null;
      draft.selectedOffersPriceCheckReady = false;
    }
  },
});

export const {
  getFlightOffers,
  getFlightsFailure,
  getFlightsSuccess,
  getFlightOffersPriceConfimation,
  getFlightsPriceConfimationSuccess,
  getFlightsPriceConfimationFailure,
  resetOfferSearchData,
  filterOffers,
  filterSuccess,
  filterFailure,
  lazyLoadOffers,
  lazyLoadOffersSucess,
  lazyLoadOffersFailure,
  filterReset,
} = flightsSlice.actions;
export default flightsSlice.reducer;
